import React, { FC } from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles, Theme, createStyles, Grid } from '@material-ui/core';
import { useTranslation } from '../../../../hooks/useTranslation';
import PhoneTextField from '../../../phone-text-field/index';
import PaperBox from '../../../utility/paper-box';
import colors from '../../../../themes/main-colors';
import { parsePhoneNumber } from '../../../../util/format';
import EmailTextField from '../../../email-text-field/index';
import Button from '@material-ui/core/Button';
import { OutageResultPanelData } from '../outage-map-panel/OutageResultsPanel';
import { NotificationRequestAccountParams } from '../../OutageCenterPage';
import { Model } from '../../report-outage/serviceLocation.rules';
import { FormState } from '../../../../hooks/useFormState.types';
import { isEqual } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    boxWrapper: {
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
      marginTop: theme.spacing(1.25),
      marginBottom: theme.spacing(3.125),
      padding: theme.spacing(2.5, 3, 3.75, 3),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
    submitButtonWrapper: {
      float: 'right',
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(2, 0, 1, 0),
        textAlign: 'center',
        float: 'none',
      },
    },
    submitButton: {
      padding: theme.spacing(1.625),
      backgroundColor: colors.orange,
      borderRadius: 5,
      color: colors.white,
      fontSize: '1.125rem',
      lineHeight: '1.125rem',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
      '&:hover': {
        backgroundColor: colors.orange,
      },
      '&:active': {
        backgroundColor: colors.orange,
      },
      '&.MuiButton-root.Mui-disabled': {
        color: colors.quickSilverGrey,
        backgroundColor: colors.disableColor,
      },
    },
    containerBox: {
      display: 'flex',
      marginTop: theme.spacing(2),
      marginLeft: -20,
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        paddingBottom: 22,
        marginLeft: 0,
      },
    },
    backButton: {
      fontSize: '1.125rem',
      lineHeight: '1.375rem',
      padding: theme.spacing(1.125, 1.875),
      float: 'left',
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'underline',
      },
      [theme.breakpoints.down('xs')]: {
        float: 'none',
        textAlign: 'center',
      },
    },
    locationText: {
      paddingBottom: theme.spacing(0.5),
      fontSize: '1rem',
      color: colors.darkBlue,
      fontWeight: 600,
      lineHeight: '1.75rem',
    },
    labelText: {
      fontSize: '.75rem',
      lineHeight: '1.31rem',
      paddingLeft: '10px',
    },
    leftSpace: {
      paddingLeft: theme.spacing(2.5),
      [theme.breakpoints.down('xs')]: {
        paddingLeft: 0,
      },
    },
    alternateLookupOptionBox: {
      paddingLeft: theme.spacing(1.25),
    },
    checkBoxStyles: {
      alignItems: 'baseline',
      padding: '10px',
      height: '100%',
    },
    cancelButton: {
      color: colors.brightBlue,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

type Props = {
  isChangeNotificationSettings?: boolean;
  onSubmit?: () => void;
  onClose?: () => void;
  data?: OutageResultPanelData;
  loggedInUserNotificationRequestAccountParams: NotificationRequestAccountParams;
  notificationFormValues: Model;
  setNotificationFormValues: React.Dispatch<React.SetStateAction<Model>>;
  setNotifFormSuccessfullySubmitted: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  form: FormState<Model>;
  handleOnSubmit: () => Promise<void>;
  isSignedInFlow: boolean;
};

const Notification: FC<Props> = ({
  isChangeNotificationSettings,
  onClose,
  form,
  handleOnSubmit,
  isSignedInFlow,
  notificationFormValues,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const disableUpdateButton =
    isEqual(notificationFormValues, form?.values) ||
    (form?.values?.receiveCallBack &&
      form?.values?.callbackPhoneNumber?.length < 10);

  return (
    <>
      <PaperBox
        className={classes.boxWrapper}
        data-testid={'notification-options'}
      >
        <>
          <Typography variant={'h2'} className={classes.locationText}>
            {t('HOW_TO_NOTIFY_POWER_RESTORED')}
          </Typography>
          <Grid xs={12} md={12} className={classes.containerBox}>
            {isSignedInFlow && Boolean(form.values.textNumber) && (
              <Grid xs={12} md={6} className={classes.leftSpace}>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'flex-start'}
                >
                  <Typography
                    variant={'subtitle1'}
                    noWrap
                    className={classes.labelText}
                  >
                    {t('TEXT_MESSAGE')}
                  </Typography>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'flex-start'}
                    alignItems={'center'}
                  >
                    <Checkbox
                      value="secondary"
                      name="receiveText"
                      checked={form.values.receiveText}
                      color={'primary'}
                      onChange={form.onChange}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      className={classes.checkBoxStyles}
                    />
                    <Box className={classes.alternateLookupOptionBox}>
                      <PhoneTextField
                        size={'small'}
                        placeholder={t('PHONE_NUMBER')}
                        label={''}
                        disabled={true}
                        key={String(form.values.textNumber)}
                        name={'textNumber'}
                        autoComplete={'telephone'}
                        type={'number'}
                        inputProps={{ type: 'telephone' }}
                        {...form.props('textNumber')}
                        error={
                          form.values.receiveText &&
                          Boolean(form.errors.receiveText)
                        }
                        helperText={
                          form.values.receiveText && form.errors.receiveText
                        }
                        value={parsePhoneNumber(form.values.textNumber)}
                        data-testid={'text-phone-number'}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
            {/*  // winter storm 2024: temporarily hide phone field
            <Grid xs={12} md={4} className={classes.leftSpace}>
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
              >
                <Typography
                  variant={'subtitle1'}
                  className={classes.labelText}
                  noWrap
                >
                  {t('PHONE_CALL')}
                </Typography>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  justifyContent={'flex-start'}
                  alignItems={'center'}
                >
                  <Checkbox
                    name="receiveCallBack"
                    checked={form.values.receiveCallBack}
                    color={'primary'}
                    onChange={form.onChange}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    className={classes.checkBoxStyles}
                  />
                  <Box className={classes.alternateLookupOptionBox}>
                    <PhoneTextField
                      size={'small'}
                      placeholder={t('PHONE_NUMBER')}
                      disabled={!form.values.receiveCallBack}
                      label={''}
                      key={String(form.values.receiveCallBack)}
                      data-testid={'callback-phone-number'}
                      name={'callbackPhoneNumber'}
                      autoComplete={'telephone'}
                      type={'number'}
                      inputProps={{ type: 'telephone' }}
                      {...form.props('callbackPhoneNumber')}
                      error={
                        form.values.receiveCallBack &&
                        Boolean(form.errors.callbackPhoneNumber)
                      }
                      helperText={
                        form.values.receiveCallBack &&
                        form.errors.callbackPhoneNumber
                      }
                      value={form.values.callbackPhoneNumber}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid> */}
            {isSignedInFlow && Boolean(form.values.email) && (
              <Grid xs={12} md={6} className={classes.leftSpace}>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'flex-start'}
                >
                  <Typography
                    variant={'subtitle1'}
                    className={classes.labelText}
                    noWrap
                  >
                    {t('EMAIL')}
                  </Typography>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'flex-start'}
                    alignItems={'center'}
                  >
                    <Checkbox
                      value="secondary"
                      name="receiveEmail"
                      checked={form.values.receiveEmail}
                      color={'primary'}
                      onChange={form.onChange}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      className={classes.checkBoxStyles}
                    />
                    <Box className={classes.alternateLookupOptionBox}>
                      <EmailTextField
                        style={{ width: '100%' }}
                        placeholder={t('EMAIL_ADDRESS')}
                        label={''}
                        size={'small'}
                        disabled={true}
                        name="email"
                        value={form.values.email}
                        error={Boolean(form.errors.email)}
                        onChange={form.onChange}
                        onBlur={form.onBlur}
                        helperText={form.errors.email}
                        data-testid={'email-address'}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      </PaperBox>
      {isChangeNotificationSettings && (
        <Grid xs={12} sm={12}>
          <Grid className={classes.backButton} xs={12} sm={6}>
            <Button
              className={classes.cancelButton}
              onClick={() => onClose && onClose()}
              disableRipple={true}
            >
              <Typography variant={'h4'} noWrap>
                {t('CANCEL')}
              </Typography>
            </Button>
          </Grid>
          <Grid xs={12} sm={6} className={classes.submitButtonWrapper}>
            <Button
              disabled={disableUpdateButton}
              size={'large'}
              className={classes.submitButton}
              onClick={handleOnSubmit}
            >
              <Typography variant={'h4'} noWrap>
                {t('SAVE_UPDATE_SETTINGS')}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default Notification;
